import React, { useState } from "react";
import { toast } from "react-toastify";
import useCheckRegisterReview from "./useCheckRegisterReview.js";
import CheckRegisterActionDialog from "./checkRegisterActionDialog.jsx";
import InvoiceStatusBadge from "./invoiceStatusBadge.jsx";
import InvoiceReviewFilters from "./invoiceReviewFilters.jsx";
import { fetchClientDocumentSignedUrl } from "../../../utils/apiUtils";

// Files carry a raw S3 key (`s3Path`); fetch a short-lived signed URL at download time.
// Open the tab synchronously so the popup is tied to the click, then redirect it once signed.
const openSignedFile = async (file) => {
    if (!file) return;
    const popup = window.open("", "_blank");
    try {
        const { url } = await fetchClientDocumentSignedUrl(file.s3Path);
        if (popup) popup.location = url;
        else window.open(url, "_blank");
    } catch {
        popup?.close();
        toast.error("Could not open the file. Please try again.");
    }
};

const PAGE_SIZE_OPTIONS = [20, 50, 100];

const formatDate = (iso) => (!iso ? "—" : iso);

const CheckRegisterReviewTab = () => {
    const {
        items,
        nextToken,
        isLoading,
        isError,
        isFetching,
        refetch,
        filters,
        setFilters,
        applyFilters,
        clearFilters,
        pageSize,
        onPageSizeChange,
        tokenStack,
        goNext,
        goPrevious,
        mutations,
        selectedIds,
        toggleRowSelection,
        toggleAllSelection,
        clearSelection,
        canBulkAccept,
        bulkActionMutation,
        bulkDownloadMutation,
    } = useCheckRegisterReview();

    const [dialogState, setDialogState] = useState(null);

    if (isError) {
        toast.error("Service unavailable.");
    }

    const openDialog = (doc, action) => setDialogState({ doc, action });
    const closeDialog = () => setDialogState(null);

    return (
        <div className="check-register-review-tab">
            <InvoiceReviewFilters
                filters={filters}
                setFilters={setFilters}
                onApply={applyFilters}
                onClear={clearFilters}
                onRefresh={refetch}
                isRefreshing={isFetching}
            />

            {selectedIds.size > 0 && (
                <div className="invoice-review__bulk-bar" role="region" aria-label="Bulk actions">
                    <span className="invoice-review__bulk-count" role="status" aria-atomic="true">
                        <span className="invoice-review__bulk-count-badge" aria-hidden="true">{selectedIds.size}</span>
                        {selectedIds.size === 1 ? "check-register selected" : "check-registers selected"}
                    </span>
                    <button
                        type="button"
                        className="invoice-review__bulk-clear"
                        onClick={clearSelection}
                        aria-label="Clear selection"
                    >
                        Clear
                    </button>
                    <button
                        type="button"
                        className="invoice-review__bulk-btn invoice-review__bulk-btn--accept"
                        disabled={!canBulkAccept || bulkActionMutation.isPending}
                        onClick={() => bulkActionMutation.mutate()}
                    >
                        Approve All
                    </button>
                    <button
                        type="button"
                        className="invoice-review__bulk-download"
                        disabled={bulkDownloadMutation.isPending}
                        onClick={() => bulkDownloadMutation.mutate()}
                    >
                        Download All
                    </button>
                </div>
            )}

            <div className="invoice-review-table-wrap">
            <div className="table-wrapper">
                <table className="invoice-review-table">
                    <thead>
                        <tr>
                            <th>
                                <input
                                    type="checkbox"
                                    aria-label="Select all in-review check-registers"
                                    onChange={toggleAllSelection}
                                    checked={selectedIds.size > 0 && selectedIds.size === items.filter((i) => i.status === 'in-review').length}
                                />
                            </th>
                            <th>Account Name</th>
                            <th>Account ID</th>
                            <th>Run Date</th>
                            <th>Period Start</th>
                            <th>Period End</th>
                            <th>Status</th>
                            <th>Invoices</th>
                            <th>Billing Report</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading && items.length === 0 ? (
                            Array.from({ length: 5 }).map((_, i) => (
                                <tr key={i} className="invoice-review-table__skeleton-row">
                                    {Array.from({ length: 10 }).map((__, j) => (
                                        <td key={j}><span className="invoice-review-table__skeleton" /></td>
                                    ))}
                                </tr>
                            ))
                        ) : items.length === 0 ? (
                            <tr>
                                <td colSpan={10} className="invoice-review-table__empty">
                                    No check-registers found for the selected filters.
                                </td>
                            </tr>
                        ) : (
                            items.map((item) => {
                                const nonPhiFile = item.files?.find((f) => !f.phi);
                                const phiFile = item.files?.find((f) => f.phi);
                                const canAccept = item.status !== "accepted";
                                const canReject = item.status !== "accepted" && item.status !== "rejected";
                                const isSelectable = item.status === 'in-review';

                                return (
                                    <tr key={item.sk} className="invoice-review-table__row">
                                        <td>
                                            {isSelectable && (
                                                <input
                                                    type="checkbox"
                                                    checked={selectedIds.has(item.sk)}
                                                    onChange={() => toggleRowSelection(item.sk)}
                                                    aria-label={`Select ${item.accountName}`}
                                                />
                                            )}
                                        </td>
                                        <td>{item.accountName}</td>
                                        <td>{item.accountHccId}</td>
                                        <td>{formatDate(item.runDate)}</td>
                                        <td>{formatDate(item.periodStart)}</td>
                                        <td>{formatDate(item.periodEnd)}</td>
                                        <td>
                                            <InvoiceStatusBadge status={item.status} aria-label={`Status: ${item.status}`} />
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                className={`invoice-review-table__download-btn${!nonPhiFile ? " opacity-50 cursor-not-allowed" : ""}`}
                                                disabled={!nonPhiFile}
                                                aria-label="Download Invoices file"
                                                title={!nonPhiFile ? "No Invoices file available" : undefined}
                                                onClick={() => openSignedFile(nonPhiFile)}
                                            >
                                                Invoices
                                            </button>
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                className={`invoice-review-table__download-btn${!phiFile ? " opacity-50 cursor-not-allowed" : ""}`}
                                                disabled={!phiFile}
                                                aria-label="Download Billing Report file"
                                                title={!phiFile ? "No Billing Report file available" : undefined}
                                                onClick={() => openSignedFile(phiFile)}
                                            >
                                                Billing Report
                                            </button>
                                        </td>
                                        <td>
                                            <div className="invoice-review-table__actions-cell">
                                                <button
                                                    type="button"
                                                    className={`invoice-review-table__action-btn invoice-review-table__action-btn--accept${!canAccept ? " opacity-50 cursor-not-allowed" : ""}`}
                                                    disabled={!canAccept}
                                                    aria-label="Accept check register"
                                                    onClick={() => canAccept && openDialog(item, "accept")}
                                                >
                                                    Accept
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`invoice-review-table__action-btn invoice-review-table__action-btn--reject${!canReject ? " opacity-50 cursor-not-allowed" : ""}`}
                                                    disabled={!canReject}
                                                    aria-label="Reject check register"
                                                    onClick={() => canReject && openDialog(item, "reject")}
                                                >
                                                    Reject
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
            </div>

            <div className="invoice-review-table__controls invoice-review-table__controls--bottom">
                <label className="invoice-review-table__page-size-label">
                    Rows per page:
                    <select
                        className="invoice-review-table__page-size"
                        value={pageSize}
                        onChange={(e) => onPageSizeChange(Number(e.target.value))}
                    >
                        {PAGE_SIZE_OPTIONS.map((n) => (
                            <option key={n} value={n}>{n}</option>
                        ))}
                    </select>
                </label>
                <div className="invoice-review-table__page-nav">
                    <button
                        type="button"
                        className="invoice-review-table__page-btn"
                        disabled={tokenStack.length === 0}
                        onClick={goPrevious}
                    >
                        Previous
                    </button>
                    <button
                        type="button"
                        className="invoice-review-table__page-btn"
                        disabled={!nextToken}
                        onClick={goNext}
                    >
                        Next
                    </button>
                </div>
            </div>

            {dialogState && (
                <CheckRegisterActionDialog
                    doc={dialogState.doc}
                    action={dialogState.action}
                    onClose={closeDialog}
                    onSuccess={closeDialog}
                    mutations={mutations}
                />
            )}
        </div>
    );
};

export default CheckRegisterReviewTab;
